<template>
    <component
        :is="noLabelWrapper ? 'div' : 'label'"
        ref="label"
        class="ds-checkbox text-ds-content-primary m-0 inline-flex w-full items-start gap-x-3"
        :class="[disabled ? 'cursor-not-allowed' : 'cursor-pointer']"
    >
        <div class="flex-shrink-0">
            <input
                v-model="model"
                :id="inputId"
                class="fixed left-offscreen"
                :disabled
                :name
                type="checkbox"
                :value
            />
            <div class="faux-checkbox transition-[background-color,border-color,outline-color]">
                <FontAwesomeIcon
                    :class="[model ? 'opacity-100' : 'opacity-0']"
                    :icon
                />
            </div>
        </div>
        <div
            class="min-w-0 flex-1"
            :class="{ 'text-content-disabled': disabled }"
        >
            <span v-if="label">{{ label }}</span>
            <slot
                v-else
                name="label"
            />
        </div>
    </component>
</template>

<script lang="ts" setup>
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';
import { computed, useTemplateRef } from 'vue';

withDefaults(
    defineProps<{
        label?: string;
        icon?: string;
        inputId?: HTMLInputElement['id'];
        name: HTMLInputElement['name'];
        noLabelWrapper?: boolean;
        value: HTMLInputElement['value'];
        disabled?: boolean;
    }>(),
    {
        icon: 'fas fa-check',
        noLabelWrapper: false,
        disabled: false,
    },
);

const model = defineModel();
const labelRef = useTemplateRef<HTMLLabelElement>('label');

const oneLineHeight = computed((): string => {
    if (labelRef.value) {
        return getComputedStyle(labelRef.value).lineHeight;
    }
    return '24px'; // default to 16px font-size times 1.5 line-height
});
</script>

<style lang="scss" scoped>
.ds-checkbox {
    --one-lh: v-bind(oneLineHeight);

    @supports (height: 1lh) {
        --one-lh: 1lh;
    }

    grid-template-rows: var(--one-lh, 24px) auto;
}

.faux-checkbox {
    border: 1px solid;
    @apply items-center justify-center rounded-[4px] border-ds-primary-400 bg-white text-[14px] text-white;
    display: inline-flex;
    height: 20px;
    width: 20px;
    outline: 4px solid transparent;
}

label:hover .faux-checkbox {
    @apply border-ds-gray-800 outline-ds-primary-200;
}

input[type='checkbox']:checked + .faux-checkbox {
    @apply border-ds-gray-800 bg-ds-gray-800;
}

input[type='checkbox']:focus + .faux-checkbox {
    @apply border-ds-gray-600 outline-ds-gray-200;
}

input[type='checkbox']:not(:disabled):active + .faux-checkbox {
    @apply border-ds-gray-600 outline-ds-gray-300;
}

input[type='checkbox']:disabled + .faux-checkbox {
    @apply border-ds-primary-200 bg-white;
}

input[type='checkbox']:disabled:checked + .faux-checkbox {
    @apply border-ds-primary-200 bg-ds-primary-200;
}

label:has(input[type='checkbox']:disabled):hover .faux-checkbox {
    @apply border-ds-primary-200 outline-transparent;
}
</style>
